import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { tap, catchError, throwError } from 'rxjs';
import { LoginResponse } from '../types/login-response.type';
import { environment } from '../../environments/environment';
import { AuthUtils } from '../utils/auth.utils';
import { AuthService } from './auth.service';

if (environment.production) {
  //console.log('Modo de produção ativo');
} else {
  //console.log('Modo de desenvolvimento ativo');
}

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private apiUrl = environment.apiUrl;
  private authService = inject(AuthService);

  constructor(private httpClient: HttpClient) {
    // LoginService initialized
  }

  login(cpf: string, password: string) {
    // Codificar credenciais para Base64
    const credentials = `${cpf}:${password}`;
    const encodedValue = AuthUtils.encodeToBase64(credentials);

    // Configurar headers com autenticação Basic
    const headers = new HttpHeaders({
      'Authorization': `Basic ${encodedValue}`,
      'Content-Type': 'application/json'
    });

  //  //console.log('🔄 Fazendo login na URL:', `${this.apiUrl}/credentials/login`);
  //  //console.log('🔐 CPF:', cpf);
  //  //console.log('📧 Headers Authorization:', `Basic ${encodedValue}`);

    // Endpoint de login é sempre /nucon/login independente do ambiente
    const loginUrl = `${this.apiUrl}/nucon/login`;
    
    return this.httpClient
      .get<LoginResponse>(loginUrl, { headers })
      .pipe(
        tap((value: LoginResponse) => {
      //    //console.log('✅ Resposta do backend:', value);
          sessionStorage.setItem('auth-token', value.data.token);
          sessionStorage.setItem('shortName', value.data.shortName);
          sessionStorage.setItem('roles', JSON.stringify(value.data.roles));
          
          // Atualizar o signal do AuthService com apenas o token
          this.authService.currentUserSig.set({
            token: value.data.token
          });
          
          // Debug do que foi salvo
      //    //console.log('💾 Dados salvos no sessionStorage:');
      //    //console.log('   Token:', sessionStorage.getItem('auth-token'));
      //    //console.log('   ShortName:', sessionStorage.getItem('shortName'));
      //    //console.log('   Roles:', sessionStorage.getItem('roles'));
        }),
        catchError(this.handleError) // ADICIONEI O TRATAMENTO DE ERRO AQUI
      );
  }

  // Método para testar a conexão com o backend
  testBackendConnection() {
    const testCredentials = 'teste:teste';
    const encodedTest = AuthUtils.encodeToBase64(testCredentials);
    
    const headers = new HttpHeaders({
      'Authorization': `Basic ${encodedTest}`,
      'Content-Type': 'application/json'
    });


    return this.httpClient.get(`${this.apiUrl}/nucon/login`, { 
      headers,
      observe: 'response' // Para ver toda a resposta
    }).pipe(
      tap(response => {
    //    //console.log('✅ Conexão bem-sucedida!');
    //    //console.log('   Status:', response.status);
    //    //console.log('   Headers:', response.headers);
    //    //console.log('   Body:', response.body);
      }),
      catchError(error => {
        console.error('❌ Erro na conexão:', error);
        return throwError(() => error);
      })
    );
  }

  private handleError(error: HttpErrorResponse) {
    // Ignorar erros de parsing quando a resposta tem status 200 (resposta não-JSON do backend)
    if (error.status === 200 && error.error instanceof ProgressEvent) {
      console.warn('⚠️ Resposta não-JSON recebida do backend (status 200). Verifique o proxy e o backend.');
      return throwError(() => new Error('Resposta inválida do servidor. Verifique se o backend está acessível.'));
    }

    console.error('❌ Erro no login:', {
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      message: error.message,
      error: error.error
    });
    
    let errorMessage = 'Erro desconhecido';
    
    if (error.status === 401) {
      errorMessage = 'CPF ou senha inválidos';
    } else if (error.status === 404) {
      errorMessage = 'Endpoint não encontrado. Verifique a URL da API.';
    } else if (error.status === 0) {
      errorMessage = 'Erro de conexão. Verifique se o backend está rodando e a configuração CORS.';
    } else if (error.status === 500) {
      errorMessage = 'Erro interno do servidor.';
    } else {
      errorMessage = `Erro ${error.status}: ${error.message}`;
    }
    
    return throwError(() => new Error(errorMessage));
  }

  getToken(): string | null {
    return sessionStorage.getItem('auth-token');
  }

  getRoles(): string[] {
    try {
      const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
  //    //console.log('🎭 Roles recuperadas:', roles);
      return roles;
    } catch (e) {
      console.error('Erro ao parsear roles:', e);
      return [];
    }
  }

  getShortName(): string | null {
    return sessionStorage.getItem('shortName');
  }

  isAdmin(): boolean {
    const roles = this.getRoles();
    return roles.includes('ROLE_ADMIN');
  }

  // Verifica se o usuário está logado
  isLoggedIn(): boolean {
    const token = this.getToken();
    const isLogged = !!token;
  //  //console.log('🔐 Usuário logado?:', isLogged);
    return isLogged;
  }

  logout(): void {
//    //console.log('🚪 Fazendo logout...');
    sessionStorage.removeItem('auth-token');
    sessionStorage.removeItem('shortName');
    sessionStorage.removeItem('roles');
//    //console.log('✅ Logout concluído');
  }

  // Método para debug - mostra todos os dados salvos
  debugAuthData(): void {
    //console.log('🐛 Debug - Dados de autenticação:');
    //console.log('   Token:', this.getToken());
    //console.log('   ShortName:', this.getShortName());
    //console.log('   Roles:', this.getRoles());
    //console.log('   IsAdmin:', this.isAdmin());
    //console.log('   IsLoggedIn:', this.isLoggedIn());
  }

  makeAuthenticatedRequest() {
    const token = this.getToken();
    if (!token) {
      console.error('❌ Token não disponível para requisição autenticada');
      return throwError(() => new Error('Token não disponível'));
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

//    //console.log('🔐 Fazendo requisição autenticada com token:', token.substring(0, 20) + '...');

    return this.httpClient.get(`${this.apiUrl}/protected`, { headers });
  }
}